import { Component, EventEmitter, inject, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { InventoryOutputDetailModel } from 'src/app/models/inventory/inventory-output-detail.model';
import { InventoryOutputModel } from 'src/app/models/inventory/inventory-output.model';
import { RemissionFromOrderDetailModel } from 'src/app/models/rental/remission-from-order-detail.model';
import { RentalStockModel } from 'src/app/models/rental/rental-stock-response.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InventoryOutputDetailCaptureTableModel } from 'src/app/models/tables/inventory/inventory-output-detail-capture-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { SalesRemissionDetailService } from 'src/app/services/inventory/sales-remission-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { SalesRemissionManualOrderComponent } from '../sales-remission-manual-order/sales-remission-manual-order.component';
import { SalesRemissionMassiveOrderComponent } from '../sales-remission-massive-order/sales-remission-massive-order.component';
import { SalesRemissionFromOrderService } from 'src/app/services/inventory/sales-remission-from-order.service';
import { MassiveOrderDetailSelectionTable } from 'src/app/models/tables/preliminaries/massive-order-detail-selection-table.model';

@Component({
  selector: 'app-sales-remission-detail',
  templateUrl: './sales-remission-detail.component.html',
  styleUrls: ['./sales-remission-detail.component.scss'],
  standalone: false
})
export class SalesRemissionDetailComponent implements OnInit {

  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly salesRemissionDetailService = inject(SalesRemissionDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly salesRemissionFromOrderService = inject(SalesRemissionFromOrderService);

  @ViewChild('articleSelection') articleSelectionModalComponent?: CustomModalComponent | null = null;
  @ViewChild('salesRemissionManualOrderSelection') salesRemissionManualOrderModalComponent?: CustomModalComponent | null = null;
  @ViewChild('salesRemissionMassiveOrderSelection') salesRemissionMassiveOrderModalComponent?: CustomModalComponent | null = null;
  @Input() disabledDetail: boolean = false;
  @Input() permissionUpdatable: boolean = false;
  @Input() quantityDetails: number = 0;
  @Input() salesRemissionModel: InventoryOutputModel = new InventoryOutputModel();
  @Input() swOrderModule: boolean = false;
  @Output() hasQuantityInZero = new EventEmitter<boolean>();
  @Output() emitQuantityDetails = new EventEmitter<number>();

  ascendant: boolean = true;
  ascendantArticle: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  columnSortArticle: string | null = null;
  componentSalesRemissionManualOrder = SalesRemissionManualOrderComponent;
  componentSalesRemissionMassiveOrder = SalesRemissionMassiveOrderComponent;
  componentTableSelection = CustomTableSelectComponent;
  dataArticle: PagedResponseModel<RentalStockModel> =
    new PagedResponseModel<RentalStockModel>();
  dataOrderArticle: PagedResponseModel<RemissionFromOrderDetailModel> =
    new PagedResponseModel<RemissionFromOrderDetailModel>();
  detailData: PagedResponseModel<InventoryOutputDetailCaptureTableModel> =
    new PagedResponseModel<InventoryOutputDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filtersArticle: ColumnResponseModel[] = [];
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
    actions: []
  });
  inputsForManualOrderModals$ = new BehaviorSubject<any>({
    salesRemissionModel: 0,
  });
  inputsForMassiveOrderModals$ = new BehaviorSubject<any>({
    inventoryOutputId: 0,
  });
  isFormView: boolean = false;
  limit: number = 10;
  limitArticle: number = 5;
  output$ = new Subject<any>();
  page: number = 1;
  pageArticle: number = 1;
  salesRemissionDetail: InventoryOutputDetailModel =
    new InventoryOutputDetailModel();
  salesRemissionDetails: InventoryOutputDetailModel[] = [];
  subscription: Subscription;
  tableActions: TableAction[] = [];

  async ngOnInit() {
    await this.getColumnsForCaptureTable();
    this.tableActions = this.getDefaultActions();
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticle(payload);
          break;
        case 'onFilterChange':
          this.onFilterChange(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    const disabledDetail = changes["disabledDetail"];
    const salesRemissionModel = changes["salesRemissionModel"];
    if (salesRemissionModel && !salesRemissionModel.firstChange) {
      await this.getSalesRemissionDetailsForCaptureTable();
      this.clearSelectionTrigger++;
    }
    if (disabledDetail && !disabledDetail.firstChange) {
      this.tableActions = this.getDefaultActions();
    }
  }

  async createMassiveOrder(inventoryOutputDetails: InventoryOutputDetailModel[]) {
    try {
      const inventoryOutputId = this.salesRemissionModel.inventoryOutputId;
      await firstValueFrom(this.salesRemissionFromOrderService.createMassiveOrder(inventoryOutputDetails, inventoryOutputId));
      await this.getSalesRemissionDetailsForCaptureTable();
      this.toastHandlerService.showToast(
        'success',
        'El detalle de la remisión de venta fue creado exitosamente'
      );
    } catch (error) {
      console.error('Error al crear el pedido masivo.', error);
    }
  }

  async deleteSalesRemissionDetail(selectedIds: number[]) {
    if (!selectedIds || selectedIds.length === 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe seleccionar al menos un registro para eliminar.'
      );
      return;
    }
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar los detalles seleccionados?');
    if (accepted) {
      try {
        await firstValueFrom(this.salesRemissionDetailService.deleteSomeDetails(selectedIds));
        await this.getSalesRemissionDetailsForCaptureTable();
        this.clearSelectionTrigger++;
        this.toastHandlerService.showToast(
          'success',
          'El detalle de la remisión de venta fue eliminado exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar el detalle de la remisión de venta.', error);
      }
    }
  }

  async getColumnsForCaptureTable() {
    try {
      if (this.detailHeader.length === 0) {
        const response = await firstValueFrom(this.salesRemissionDetailService.getColumnsForCaptureTable());
        this.detailHeader = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsForListStocksInShopByInventory() {
    try {
      if (this.headerArticle.length === 0) {
        const response = await firstValueFrom(this.salesRemissionDetailService.getColumnsForListStocksInShopByInventory());
        this.headerArticle = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de artículos.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    if (this.disabledDetail) {
      return [];
    } else {
      return [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        }
      ];
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }


  async getListStocksInShopByInventory() {
    try {
      const response = await firstValueFrom(this.salesRemissionDetailService.getListStocksInShopByInventoryAsync(
        this.salesRemissionModel.inventoryOutputId, this.pageArticle, this.limitArticle, this.columnSortArticle,
        this.ascendantArticle, this.filtersArticle));
      this.dataArticle = response;
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  async getSalesRemissionDetailById(id: number): Promise<InventoryOutputDetailModel> {
    try {
      const response = await firstValueFrom(this.salesRemissionDetailService.getSalesRemissionDetailById(id));
      this.getFormView(true);
      return response;
    } catch (error) {
      console.error('Error al obtener el detalle por Id.', error);
      return new InventoryOutputDetailModel();
    }
  }

  async getSalesRemissionDetailByKeys(storeId: number, articleId: number): Promise<InventoryOutputDetailModel> {
    try {
      const response = await firstValueFrom(this.salesRemissionDetailService.getSalesRemissionDetailByKeys(storeId, articleId,
        this.salesRemissionModel.inventoryOutputId));
      this.getFormView(true);
      return response;
    } catch (error) {
      console.error('Error al obtener el detalle por claves.', error);
      return new InventoryOutputDetailModel();
    }
  }

  async getSalesRemissionDetailsForCaptureTable() {
    try {
      const response = await firstValueFrom(this.salesRemissionDetailService.getSalesRemissionDetailsForCaptureTable(
        this.page, this.limit, this.columnSort, this.ascendant, this.salesRemissionModel.inventoryOutputId
      ));
      this.getFormView(false);
      this.detailData = response;
      this.emitQuantityDetails.emit(response.totalItems);
      this.hasQuantityInZero.emit(response.items.some(detail => detail.quantity === 0));
    } catch (error) {
      console.error('Error al buscar los detalles de la remisión de venta.', error);
    }
  }

  async handleMassiveOrderSelection(selectedItems: MassiveOrderDetailSelectionTable[]) {
    if (!selectedItems?.length) return;
    const newDetails = selectedItems.map((item: MassiveOrderDetailSelectionTable) => {
      const detail: InventoryOutputDetailModel = new InventoryOutputDetailModel();
      detail.inventoryOutputId = this.salesRemissionModel.inventoryOutputId;
      detail.storeId = item.storeId;
      detail.articleId = item.articleId;
      detail.orderDetailId = item.orderDetailId;
      return detail;
    });

    await this.createMassiveOrder(newDetails);
  }

  async handleSelectedItem(selectedItems: any[]) {
    if (selectedItems.length === 1) {
      const [{ storeId, articleId, orderDetailId }] = selectedItems;
      const response = await this.getSalesRemissionDetailByKeys(storeId, articleId);
      if (response.inventoryOutputDetailId === 0) {
        if (orderDetailId && this.swOrderModule) {
          response.orderDetailId = orderDetailId;
        }
        this.salesRemissionDetail = response;
      }
    } else {
      this.salesRemissionDetails = selectedItems.map((item) => {
        const detail = new InventoryOutputDetailModel();
        if (item.orderDetailId && this.swOrderModule) {
          detail.orderDetailId = item.orderDetailId;
        }
        const inventoryOutputId = this.salesRemissionModel.inventoryOutputId;
        detail.inventoryOutput = this.salesRemissionModel;
        detail.inventoryOutputId = inventoryOutputId;
        detail.articleId = item.articleId;
        detail.storeId = item.storeId;
        return detail;
      });
    }
  }

  async onAction(event: { action: string, item?: InventoryOutputDetailCaptureTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit') {
      const response = await this.getSalesRemissionDetailById(event.item!.id);
      if (response) { this.salesRemissionDetail = response; }
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      await this.deleteSalesRemissionDetail(event.selectedItems);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getSalesRemissionDetailsForCaptureTable();
  }

  async onPageChangeArticle(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageArticle = event.page;
    this.limitArticle = event.pageSize;
    this.columnSortArticle = event.sortField || null;
    this.ascendantArticle = event.sortOrder === 1;
    await this.getListStocksInShopByInventory();
  }

  async openArticleSelectionModal() {
    this.filtersArticle = [];
    await this.getColumnsForListStocksInShopByInventory();
    await this.getListStocksInShopByInventory();
    this.updateInputs();
    this.articleSelectionModalComponent?.show();
  }

  openManualOrderSelectionModal() {
    if (!this.salesRemissionModel.inventoryOutputId) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe guardar la remisión de venta antes de seleccionar los detalles por pedido.'
      );
      return;
    }
    this.inputsForManualOrderModals$.next({
      salesRemissionModel: this.salesRemissionModel
    });
    this.salesRemissionManualOrderModalComponent?.show();
  }

  openMassiveOrderSelectionModal() {
    if (!this.salesRemissionModel.inventoryOutputId) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe guardar la remisión de venta antes de seleccionar los detalles por pedido.'
      );
      return;
    }
    this.inputsForMassiveOrderModals$.next({
      inventoryOutputId: this.salesRemissionModel.inventoryOutputId
    });
    this.salesRemissionMassiveOrderModalComponent?.show();
  }

  processFilter(filter: any) {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersArticle.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInputs() {
    this.inputsForArticlesSelection$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      limitPaginator: true,
      filters: this.filtersArticle,
      actions: [
        {
          label: 'Imágenes',
          icon: 'pi pi-image',
          action: 'view-image'
        }
      ]
    });
  }
}
